import { EnemyInstance } from "../data/enemies";
import { EnemyFactory } from "../data/enemyFactory";
import { EnemyId } from "./runState";
import { createDeterministicRng } from "./simulationHelpers";
import { getAutomationOverride } from "../automation/automationTags";
import { BossFactory, BossId } from "../data/bossFactory";

export const MAX_ENCOUNTER_ENEMIES = 3;
const MIN_ENCOUNTER_ENEMIES = 1;

export type EncounterEnemy = {
  id: EnemyId | BossId;
  nameSuffix?: string;
};

export type EncounterEnemyConfig = {
  enemies: EncounterEnemy[];
};

type BuildEncounterEnemiesOptions = {
  floor: number;
  config?: EncounterEnemyConfig | null;
};

const createInstanceFromConfig = (enemy: EncounterEnemy, floor: number): EnemyInstance | null => {
  try {
    // Check if this is a boss ID (starts with "boss_")
    if (enemy.id.startsWith("boss_")) {
      return BossFactory.create(enemy.id as BossId, {
        floor,
      });
    }
    // Regular enemy
    return EnemyFactory.create(enemy.id as EnemyId, {
      nameSuffix: enemy.nameSuffix,
      floor,
    });
  } catch {
    return null;
  }
};

export const buildEncounterEnemies = (options: BuildEncounterEnemiesOptions): EnemyInstance[] => {
  let rng = Math.random;
  const seed = getAutomationOverride<number | string>("seed");
  if (seed !== undefined) {
    rng = createDeterministicRng(typeof seed === "string" ? parseInt(seed, 10) : seed);
  }

  if (options.config && options.config.enemies.length > 0) {
    const enemies = options.config.enemies
      .slice(0, MAX_ENCOUNTER_ENEMIES)
      .map((enemy) => createInstanceFromConfig(enemy, options.floor))
      .filter((enemy): enemy is EnemyInstance => Boolean(enemy));

    return enemies;
  }

  // Slow the ramp of enemy count to smooth difficulty:
  // 1 enemy floors 1-3, 2 enemies floors 4-6, 3 enemies from 7+
  const defaultCount = Math.min(
    Math.max(1 + Math.floor((options.floor - 1) / 3), MIN_ENCOUNTER_ENEMIES),
    MAX_ENCOUNTER_ENEMIES
  );

  // For early floors (1-3), sometimes spawn bats instead of goblins (40% chance)
  const enemyId: EnemyId = options.floor <= 3 && rng() < 0.4 ? "bat" : "goblin";

  return EnemyFactory.createGroup(enemyId, defaultCount, { floor: options.floor });
};
